import React, { useEffect } from 'react';
import { McButton, McIcon, McTooltip } from '@maersk-global/mds-react-wrapper';
import { SchemaExplorer } from './SchemaExplorer';

export interface SchemaExplorerPanelProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    terminalId: string;
    databaseType?: string;
    /** Snippet insertion callback. When omitted, Schema Explorer rows fall back to clipboard copy. */
    onInsertSnippet?: (text: string) => void;
    /** Optional handler — when provided, the panel header shows an "Open fullscreen" button. */
    onOpenFullscreen?: () => void;
    /** Optional handler — when provided, the panel header shows an "Edit query" button (Overview use case). */
    onEditQuery?: () => void;
    /** Identifies the page so localStorage state stays scoped (informational here, persisted via useSchemaPanelState in callers). */
    variant?: 'create' | 'edit' | 'overview';
    /** When set, jumps the embedded SchemaExplorer to this tab (driven by toolbar tab shortcuts). */
    activeTab?: 'schema' | 'field-search' | 'field-catalog';
    /** Pixel width of the docked panel. Defaults to 420px. */
    width?: number;
    /** Top offset to clear app header. Defaults to 64px. */
    topOffset?: number;
}

/**
 * Right-docked, collapsible Schema Explorer host. Mirrors the styling pattern
 * used by `QueryListSidebar` for visual consistency across the app.
 * - Renders a floating toggle handle on the right edge when collapsed.
 * - Renders a drawer (with Schema Explorer content) when open.
 * - Pointer-events on the toggle/drawer; the rest of the page stays interactive.
 */
export const SchemaExplorerPanel: React.FC<SchemaExplorerPanelProps> = ({
    open,
    onOpenChange,
    terminalId,
    databaseType,
    onInsertSnippet,
    onOpenFullscreen,
    onEditQuery,
    width = 420,
    topOffset = 64,
    activeTab,
}) => {
    // Esc closes the panel.
    useEffect(() => {
        if (!open) return;
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') onOpenChange(false);
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [open, onOpenChange]);

    return (
        <>
            {/* Docked drawer — opened/closed via toolbar button */}
            <aside
                id="schema-explorer-panel"
                role="complementary"
                aria-label="Schema Explorer"
                style={{
                    position: 'fixed',
                    top: topOffset,
                    bottom: 0,
                    right: open ? 0 : -width,
                    width,
                    background: 'var(--mds-color-white-background, #ffffff)',
                    borderLeft: '1px solid var(--mds-color-neutral-20, #e5e7eb)',
                    boxShadow: open ? '-2px 0 8px rgba(0,0,0,0.06)' : 'none',
                    transition: 'right 0.3s ease',
                    zIndex: 999,
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden',
                }}
            >
                {/* Panel header */}
                <div
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        padding: '8px 12px',
                        borderBottom: '1px solid var(--mds-color-neutral-15, #ebebeb)',
                        background: 'var(--mds-color-neutral-05, #fafafa)',
                        flexShrink: 0,
                    }}
                >
                    <McIcon icon="database" />
                    <span style={{ fontWeight: 600, fontSize: '0.9rem', flexGrow: 1 }}>
                        Schema Explorer
                    </span>
                    {onEditQuery && (
                        <McTooltip>
                            <McButton
                                slot="trigger"
                                variant="plain"
                                fit="small"
                                aria-label="Edit query"
                                onClick={onEditQuery}
                            >
                                <McIcon icon="pencil" size="20" />
                            </McButton>
                            <span>Edit query</span>
                        </McTooltip>
                    )}
                    {onOpenFullscreen && (
                        <McTooltip>
                            <McButton
                                slot="trigger"
                                variant="plain"
                                fit="small"
                                aria-label="Open in fullscreen"
                                onClick={onOpenFullscreen}
                            >
                                <McIcon icon="arrow-up-right-and-arrow-down-left-from-center" size="20" />
                            </McButton>
                            <span>Open in fullscreen</span>
                        </McTooltip>
                    )}
                    <McTooltip>
                        <McButton
                            slot="trigger"
                            variant="plain"
                            fit="small"
                            aria-label="Close panel"
                            onClick={() => onOpenChange(false)}
                        >
                            <McIcon icon="times" size="20" />
                        </McButton>
                        <span>Close panel</span>
                    </McTooltip>
                </div>

                {/* Panel body — Schema Explorer in embedded mode.
                    Mounted only while `open` to avoid background data fetches when collapsed. */}
                {open && (
                    <div style={{ flex: 1, overflow: 'auto', padding: '12px' }}>
                        <SchemaExplorer
                            open
                            onClose={() => onOpenChange(false)}
                            terminalId={terminalId}
                            databaseType={databaseType}
                            renderMode="embedded"
                            onInsertSnippet={onInsertSnippet}
                            defaultTab={activeTab}
                        />
                    </div>
                )}
            </aside>
        </>
    );
};
